import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import type { User, AuthState } from '../types'

interface TenantInfo {
  id: string
  slug: string
  name: string | null
  role: string
}

// Available tenant for tenant switcher
interface AvailableTenant {
  id: string
  slug: string
  name: string | null
  role: string
}

interface VerifyCodeResult {
  success: boolean
  requires_2fa?: boolean
  mfa_session_token?: string
}

interface AuthContextType extends AuthState {
  // Magic link flow (Step 1)
  requestMagicLink: (email: string) => Promise<{ success: boolean; message: string }>
  resendMagicLink: (email: string) => Promise<{ success: boolean; message: string }>
  validateMagicLink: (token: string) => Promise<{
    email: string
    tenants: TenantInfo[]
    magic_link_id: string
  }>
  // Tenant selection
  setTenant: (tenant: { slug: string; name: string | null }) => void
  getTenant: () => { slug: string; name: string | null } | null
  // Available tenants for tenant switcher
  storeAvailableTenants: (tenants: AvailableTenant[]) => void
  getAvailableTenants: () => AvailableTenant[]
  // Tenant switching (for multi-tenant users)
  switchTenant: (targetTenantSlug: string) => Promise<void>
  isSwitchingTenant: boolean
  // PIN verification (Step 2 - after tenant selection)
  requestCode: (email: string) => Promise<{ session_token: string }>
  verifyCode: (sessionToken: string, code: string) => Promise<VerifyCodeResult>
  resendCode: (sessionToken: string) => Promise<void>
  // 2FA verification (Step 3 - if 2FA is enabled)
  verify2FA: (mfaSessionToken: string, code: string) => Promise<void>
  // Account status
  checkLockoutStatus: (email: string) => Promise<{
    success: boolean
    locked: boolean
    locked_until?: number
    remaining_minutes?: number
  }>
  // Session management
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: api.getToken(),
    isAuthenticated: false,
    isLoading: true,
  })
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false)

  const checkAuth = useCallback(async () => {
    const token = api.getToken()
    if (!token) {
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
      return
    }

    try {
      const response = await api.getMe()
      if (response.success && response.user) {
        setState({
          user: response.user as User,
          token,
          isAuthenticated: true,
          isLoading: false,
        })
      } else {
        api.setToken(null)
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        })
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      api.setToken(null)
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Magic link flow
  const requestMagicLink = async (email: string) => {
    const response = await api.requestMagicLink(email)
    return response
  }

  const resendMagicLink = async (email: string) => {
    const response = await api.resendMagicLink(email)
    return response
  }

  const validateMagicLink = async (token: string) => {
    const response = await api.validateMagicLink(token)
    return {
      email: response.email,
      tenants: response.tenants,
      magic_link_id: response.magic_link_id,
    }
  }

  const setTenant = (tenant: { slug: string; name: string | null }) => {
    api.setTenant(tenant)
  }

  const getTenant = () => {
    return api.getTenant()
  }

  const storeAvailableTenants = (tenants: AvailableTenant[]) => {
    api.setAvailableTenants(tenants)
  }

  const getAvailableTenants = (): AvailableTenant[] => {
    return api.getAvailableTenants()
  }

  const requestCode = async (email: string) => {
    // Don't set global isLoading here - Login component manages its own loading state
    // Setting isLoading here would cause PublicRoute to unmount Login, losing local state
    const response = await api.requestCode(email)
    return response
  }

  const verifyCode = async (sessionToken: string, code: string): Promise<VerifyCodeResult> => {
    // Don't set global isLoading here - PublicRoute would unmount MagicLinkVerify mid-flow,
    // which restarts login and drops the 2FA step (#394). The page manages its own loading state.
    try {
      const response = await api.verifyCode(sessionToken, code)

      // Check if 2FA is required
      if (response.success && response.requires_2fa && response.mfa_session_token) {
        return {
          success: true,
          requires_2fa: true,
          mfa_session_token: response.mfa_session_token,
        }
      }

      if (response.success && response.token) {
        // Store token in API client and localStorage
        api.setToken(response.token)
        localStorage.setItem('grc_session', response.sessionId!)

        // Fetch complete user profile (includes first_name, last_name, phone)
        // The verify-code response only has basic user info
        const fullUserResponse = await api.getMe()
        const fullUser = fullUserResponse.success && fullUserResponse.user
          ? fullUserResponse.user
          : response.user

        setState({
          user: fullUser as User,
          token: response.token,
          isAuthenticated: true,
          isLoading: false,
        })

        return { success: true }
      } else {
        throw new Error('Verification failed')
      }
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }))
      throw error
    }
  }

  const verify2FA = async (mfaSessionToken: string, code: string) => {
    // Don't set global isLoading here - PublicRoute would unmount MagicLinkVerify mid-flow,
    // which restarts login and drops the 2FA step (#394). The page manages its own loading state.
    try {
      const response = await api.verify2FALogin(mfaSessionToken, code)
      if (response.success && response.token) {
        // Store token in API client and localStorage
        api.setToken(response.token)
        localStorage.setItem('grc_session', response.sessionId!)

        // Fetch complete user profile (includes first_name, last_name, phone)
        const fullUserResponse = await api.getMe()
        const fullUser = fullUserResponse.success && fullUserResponse.user
          ? fullUserResponse.user
          : response.user

        setState({
          user: fullUser as User,
          token: response.token,
          isAuthenticated: true,
          isLoading: false,
        })
      } else {
        throw new Error(response.message || 'Two-factor verification failed')
      }
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }))
      throw error
    }
  }

  const resendCode = async (sessionToken: string) => {
    try {
      await api.resendCode(sessionToken)
    } catch (error) {
      throw error
    }
  }

  const checkLockoutStatus = async (email: string) => {
    const response = await api.checkLockoutStatus(email)
    return response
  }

  const logout = async () => {
    try {
      await api.logout()
    } finally {
      // Clear tenant context and available tenants on logout
      api.clearTenant()
      api.clearAvailableTenants()
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  }

  /**
   * Switch to a different tenant.
   * This gets a switch token from central-admin, then exchanges it for a new JWT on the target tenant.
   */
  const switchTenant = async (targetTenantSlug: string) => {
    if (!state.user?.email || !state.token) {
      throw new Error('You must be logged in to switch tenants')
    }

    // Find target tenant in available tenants
    const availableTenants = api.getAvailableTenants()
    const targetTenant = availableTenants.find(t => t.slug === targetTenantSlug)

    if (!targetTenant) {
      throw new Error('You do not have access to this organization')
    }

    // Store current tenant for rollback in case of failure
    const currentTenant = api.getTenant()

    setIsSwitchingTenant(true)

    try {
      // Step 1: Get switch token from central-admin
      const switchResponse = await api.requestTenantSwitch(state.token, targetTenantSlug, state.user.email)

      if (!switchResponse.success || !switchResponse.switch_token) {
        throw new Error(switchResponse.message || 'Failed to initiate tenant switch')
      }

      // Step 2: Update tenant context to target tenant
      api.setTenant({ slug: targetTenant.slug, name: targetTenant.name })

      // Step 3: Exchange switch token for new JWT on target tenant
      const tokenResponse = await api.completeTenantSwitch(switchResponse.switch_token)

      if (!tokenResponse.success || !tokenResponse.token) {
        // Revert tenant context on failure
        if (currentTenant) {
          api.setTenant(currentTenant)
        }
        throw new Error(tokenResponse.message || 'Failed to complete tenant switch')
      }

      // Step 4: Store new token and session
      api.setToken(tokenResponse.token)
      if (tokenResponse.sessionId) {
        localStorage.setItem('grc_session', tokenResponse.sessionId)
      }

      // Step 5: Fetch user profile for the new tenant
      const fullUserResponse = await api.getMe()
      const fullUser = fullUserResponse.success && fullUserResponse.user
        ? fullUserResponse.user
        : tokenResponse.user

      setState({
        user: fullUser as User,
        token: tokenResponse.token,
        isAuthenticated: true,
        isLoading: false,
      })

      // Step 6: Reload the app to clear any cached data from the previous tenant
      window.location.reload()
    } finally {
      setIsSwitchingTenant(false)
    }
  }

  return (
    <AuthContext.Provider value={{
      ...state,
      requestMagicLink,
      resendMagicLink,
      validateMagicLink,
      setTenant,
      getTenant,
      storeAvailableTenants,
      getAvailableTenants,
      switchTenant,
      isSwitchingTenant,
      requestCode,
      verifyCode,
      resendCode,
      verify2FA,
      checkLockoutStatus,
      logout,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
